import { readQuoteStatusRefusal, readVersionConflict } from '@/services/apiError';
import type { BasketPricingInput, PricedBasket } from '../types/basketPricing.types';
import type { PaymentSelection } from '../types/schedule.types';
import { decimalToMinorUnits } from '../utils/decimalMoney';
import {
    PAYMENT_AMOUNT_STALE_ERROR_NAME,
    PAYMENT_RECONFIRM_REASON,
    isExplainedAmountRefusal,
} from '../utils/paymentAmountReconfirm';

export const QUOTE_VERSION_CONFLICT_CODE = 'quote_version_conflict';

export class QuoteConflictError extends Error {
    readonly currentVersion: number | undefined;
    readonly quoteStatus: string | undefined;
    readonly status = 409;
    readonly code = QUOTE_VERSION_CONFLICT_CODE;

    constructor(currentVersion: number | undefined, quoteStatus: string | undefined, message?: string) {
        super(message || 'The quote has been changed since it was loaded.');
        this.name = 'QuoteConflictError';
        this.currentVersion = currentVersion;
        this.quoteStatus = quoteStatus;
    }
}

export class RetiredSaveError extends Error {
    constructor() {
        super('The quote draft changed before this save could finish.');
        this.name = 'RetiredSaveError';
    }
}

export const UNSAVEABLE_DRAFT_MESSAGE =
    'Your unsaved changes could not be saved yet. They are kept on this page.';

export class UnsaveableDraftError extends Error {
    constructor() {
        super(UNSAVEABLE_DRAFT_MESSAGE);
        this.name = 'UnsaveableDraftError';
    }
}

export class PaymentAmountStaleError extends Error {
    readonly status = 422;
    readonly grossMinor: number;

    constructor(grossMinor: number) {
        super(PAYMENT_RECONFIRM_REASON);
        this.grossMinor = grossMinor;
        this.name = PAYMENT_AMOUNT_STALE_ERROR_NAME;
    }
}

interface ErrorWithStatus {
    status?: unknown;
    code?: unknown;
    message?: unknown;
}

const isVersionConflict = (error: unknown): boolean => {
    if (typeof error !== 'object' || error === null) return false;
    const { status, code } = error as ErrorWithStatus;
    return status === 409 && code === QUOTE_VERSION_CONFLICT_CODE;
};

const messageOf = (error: unknown): string | undefined => {
    const { message } = error as ErrorWithStatus;
    return typeof message === 'string' ? message : undefined;
};

export const toQuoteSaveError = (error: unknown): unknown => {
    if (isVersionConflict(error)) {
        const conflict = readVersionConflict(error);
        return new QuoteConflictError(conflict?.currentVersion, conflict?.status, messageOf(error));
    }
    const terminalStatus = readQuoteStatusRefusal(error);
    if (terminalStatus) return new QuoteConflictError(undefined, terminalStatus, messageOf(error));
    return error;
};

const failedPricingInputs = new WeakMap<object, BasketPricingInput | null>();

export const recordFailedPricingInput = (error: unknown, input: BasketPricingInput | null): void => {
    if (typeof error === 'object' && error !== null) failedPricingInputs.set(error, input);
};

export const failedPricingInputOf = (error: unknown): BasketPricingInput | null | undefined =>
    typeof error === 'object' && error !== null ? failedPricingInputs.get(error) : undefined;

export const classifyAmountRefusal = (
    error: unknown,
    selection: PaymentSelection | undefined,
    pricing: PricedBasket,
): unknown => {
    const grossMinor = decimalToMinorUnits(pricing.totals.gross);
    return isExplainedAmountRefusal(error, selection, grossMinor)
        ? new PaymentAmountStaleError(grossMinor)
        : error;
};
